/**
 * Module-local auth network configuration + BroadcastChannel singleton.
 *
 * Carved out of crossTabCoordinator.ts so performRefresh.ts can import the
 * network getters without going through crossTabCoordinator (which depends on
 * performRefresh — that path created a no-circular violation).
 *
 * `configureAuthNetwork({baseUrl, aud})` is the one-time init called by app
 * bootstrap (createAppRoot, Plan 05); the rest are pure getters that throw
 * if the bootstrap hasn't run yet.
 */
import { AUTH_CHANNEL_NAME, type AuthBroadcastMessage } from "../channel/broadcastSchema.js";
import type { Audience } from "../types.js";

let _baseUrl: string | null = null;
let _aud: Audience = "admin";

export function configureAuthNetwork(opts: {
  baseUrl: string;
  /**
   * App audience. Selects the refresh path:
   *   - "platform" → POST /api/v1/_platform/auth/refresh   (CC-53 / be-047)
   *   - "admin" | "mobile" | "accounting" → POST /api/v1/auth/refresh
   * Defaults to "admin" for back-compat with existing tenant apps.
   */
  aud?: Audience;
}): void {
  const aud = opts.aud ?? "admin";
  // The cross-tab channel is named after the audience. Re-pointing the
  // audience (the global login's probe does, per attempt and at boot) must
  // re-point the channel too, or a mobile refresh posts its token — or a
  // FAMILY_REVOKED — to every open HR Admin tab.
  // Known limit: listeners on the old channel (the cooldown mirror, AuthProvider)
  // are dropped, not moved; the cooldown still rides localStorage.
  if (bc !== null && aud !== _aud) {
    bc.close();
    bc = null;
  }
  _baseUrl = opts.baseUrl;
  _aud = aud;
}

export function getAuthBaseUrl(): string {
  if (_baseUrl === null) {
    throw new Error(
      "Auth network not configured. Call configureAuthNetwork({ baseUrl }) during bootstrap before any auth action runs.",
    );
  }
  return _baseUrl;
}

/**
 * Refresh URL path appropriate for the configured audience. Platform tokens
 * route through the `/_platform/*` chain (be-047 / CC-53); tenant tokens go
 * through the legacy `/auth/refresh` path. Body shape is identical on both.
 */
export function getRefreshPath(): string {
  return _aud === "platform" ? "/api/v1/_platform/auth/refresh" : "/api/v1/auth/refresh";
}

/**
 * Logout URL path appropriate for the configured audience. Mirrors the same
 * platform/tenant split as the refresh path — backend rejects a tenant logout
 * call with a platform-aud token (returns 400) and vice versa.
 */
export function getLogoutPath(): string {
  return _aud === "platform" ? "/api/v1/_platform/auth/logout" : "/api/v1/auth/logout";
}

/**
 * Audience the auth network was configured with. Lets bootstrap code skip
 * tenant-only calls (e.g. /users/me/effective-permissions, /employees/me)
 * when running under a platform token, where those endpoints 401 by design.
 */
export function getAudience(): Audience {
  return _aud;
}

/**
 * Whether a session of `aud` may read the HR self endpoints the session
 * bootstrap calls (`/users/me/effective-permissions`, `/employees/me`). A
 * platform bearer and an accounting bearer are both refused there.
 */
export function hasHrReads(aud: Audience): boolean {
  return aud !== "platform" && aud !== "accounting";
}

/**
 * `X-Refresh-Audience` routing hint (CC-277). The tenant web apps (`admin` /
 * `mobile` / `accounting`) share a host + cookie path, so the backend can't
 * infer which per-audience refresh cookie (`linkhr_refresh_admin` /
 * `linkhr_refresh_mobile` / `linkhr_refresh_accounting`) to read without this
 * header. Sent on refresh / logout / `/me/sessions*` /
 * `/me/password`. It's a hint only — the backend still re-derives the token's
 * true audience from its family and rejects a mismatch (a spoofed header earns
 * a self-inflicted 401, never cross-audience access). Platform is a separate
 * host/path, so it sends nothing.
 */
export function getAudienceHeader(): Record<string, string> {
  return _aud === "platform" ? {} : { "X-Refresh-Audience": _aud };
}

/**
 * `Accept-Language` from the ACTIVE UI locale (`<html lang>`, which the apps
 * set on language toggle) — the auth actions fetch outside the api-client
 * middleware chain, so without this every RFC 7807 `title` they surface came
 * back in the BROWSER's language, not the one the user is reading the app in
 * (found live: an Arabic UI showing "Use the employee app" in English).
 * SSR/test-safe: no document → no header → backend default.
 */
export function getAcceptLanguageHeader(): Record<string, string> {
  if (typeof document === "undefined") return {};
  const lang = document.documentElement.lang;
  return lang === "ar" || lang === "en" ? { "Accept-Language": lang } : {};
}

let bc: BroadcastChannel | null = null;

/** The cross-tab channel `aud`'s tabs listen on. */
export function authChannelName(aud: Audience): string {
  return `${AUTH_CHANNEL_NAME}:${aud}`;
}

export function getAuthChannel(): BroadcastChannel {
  if (bc === null) {
    // Namespace the cross-tab channel per audience so a token refresh or a
    // revoked family in one app doesn't reach another app's tabs on the same
    // origin (CC-277 FE defense-in-depth). Platform is already a separate host.
    bc = new BroadcastChannel(authChannelName(_aud));
  }
  return bc;
}

export function subscribeToAuthChannel(handler: (msg: AuthBroadcastMessage) => void): () => void {
  const channel = getAuthChannel();
  const listener = (event: MessageEvent<AuthBroadcastMessage>): void => {
    handler(event.data);
  };
  channel.addEventListener("message", listener);
  return () => {
    channel.removeEventListener("message", listener);
  };
}
